import { useMutation, useQueryClient } from '@tanstack/react-query';
import { autorizacaoService } from '@/services/autorizacao';
import { toast } from '@/components/Toast';
import type { DemandaAutorizacao } from './useAutorizacoesQuery';

/**
 * Hook para aprovar uma autorização de demanda
 */
export const useAprovarAutorizacao = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { demandaAutorizacaoId: number; comentario?: string; demandaId: number }) => {
      return autorizacaoService.aprovar(params.demandaAutorizacaoId, params.comentario);
    },
    onMutate: async ({ demandaAutorizacaoId, demandaId }) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({ queryKey: ['demanda-autorizacoes', demandaId] });
      await queryClient.cancelQueries({ queryKey: ['demanda', demandaId] });

      // Snapshot dos dados atuais
      const previousAutorizacoes = queryClient.getQueryData(['demanda-autorizacoes', demandaId]);
      const previousDemanda = queryClient.getQueryData(['demanda', demandaId]);

      // Atualização otimista - autorizações
      queryClient.setQueryData(['demanda-autorizacoes', demandaId], (old: any) => {
        if (!old?.autorizacoes) return old;
        return {
          ...old,
          autorizacoes: old.autorizacoes.map((aut: DemandaAutorizacao) =>
            aut.id === demandaAutorizacaoId
              ? { ...aut, status: 'APROVADA' as const, usuario_fk: null }
              : aut
          ),
        };
      });

      // Atualização otimista - demanda
      queryClient.setQueryData(['demanda', demandaId], (old: any) => {
        if (!old?.demanda_autorizacao) return old;
        return {
          ...old,
          demanda_autorizacao: old.demanda_autorizacao.map((aut: DemandaAutorizacao) =>
            aut.id === demandaAutorizacaoId
              ? { ...aut, status: 'APROVADA' as const, usuario_fk: null }
              : aut
          ),
        };
      });

      return { previousAutorizacoes, previousDemanda };
    },
    onSuccess: (_data: any) => {
      toast.success('Autorização aprovada com sucesso!');
      // Invalidar queries relacionadas para garantir sincronização
      queryClient.invalidateQueries({ queryKey: ['demandas'] });
      queryClient.invalidateQueries({ queryKey: ['demanda-autorizacoes'] });
      queryClient.invalidateQueries({ queryKey: ['demanda'] });
    },
    onError: (error: any, _variables, context) => {
      const message = error.message || 'Erro ao aprovar autorização';
      toast.error(message);
      console.error('Erro ao aprovar autorização:', error);
      
      // Reverter atualização otimista
      if (context?.previousAutorizacoes) {
        queryClient.setQueryData(
          ['demanda-autorizacoes', _variables.demandaId],
          context.previousAutorizacoes
        );
      }
      if (context?.previousDemanda) {
        queryClient.setQueryData(['demanda', _variables.demandaId], context.previousDemanda);
      }
    },
  });
};

/**
 * Hook para reprovar uma autorização de demanda
 */
export const useReprovarAutorizacao = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { demandaAutorizacaoId: number; comentario?: string; demandaId: number }) => {
      return autorizacaoService.reprovar(params.demandaAutorizacaoId, params.comentario);
    },
    onMutate: async ({ demandaAutorizacaoId, demandaId }) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({ queryKey: ['demanda-autorizacoes', demandaId] });
      await queryClient.cancelQueries({ queryKey: ['demanda', demandaId] });

      // Snapshot dos dados atuais
      const previousAutorizacoes = queryClient.getQueryData(['demanda-autorizacoes', demandaId]);
      const previousDemanda = queryClient.getQueryData(['demanda', demandaId]);

      // Atualização otimista - autorizações
      queryClient.setQueryData(['demanda-autorizacoes', demandaId], (old: any) => {
        if (!old?.autorizacoes) return old;
        return {
          ...old,
          autorizacoes: old.autorizacoes.map((aut: DemandaAutorizacao) =>
            aut.id === demandaAutorizacaoId
              ? { ...aut, status: 'REPROVADA' as const, usuario_fk: null }
              : aut
          ),
        };
      });

      // Atualização otimista - demanda
      queryClient.setQueryData(['demanda', demandaId], (old: any) => {
        if (!old?.demanda_autorizacao) return old;
        return {
          ...old,
          demanda_autorizacao: old.demanda_autorizacao.map((aut: DemandaAutorizacao) =>
            aut.id === demandaAutorizacaoId
              ? { ...aut, status: 'REPROVADA' as const, usuario_fk: null }
              : aut
          ),
        };
      });

      return { previousAutorizacoes, previousDemanda };
    },
    onSuccess: (_data: any) => {
      toast.error('Autorização reprovada.');
      // Invalidar queries relacionadas para garantir sincronização
      queryClient.invalidateQueries({ queryKey: ['demandas'] });
      queryClient.invalidateQueries({ queryKey: ['demanda-autorizacoes'] });
      queryClient.invalidateQueries({ queryKey: ['demanda'] });
    },
    onError: (error: any, _variables, context) => {
      const message = error.message || 'Erro ao reprovar autorização';
      toast.error(message);
      console.error('Erro ao reprovar autorização:', error);
      
      // Reverter atualização otimista
      if (context?.previousAutorizacoes) {
        queryClient.setQueryData(
          ['demanda-autorizacoes', _variables.demandaId],
          context.previousAutorizacoes
        );
      }
      if (context?.previousDemanda) {
        queryClient.setQueryData(['demanda', _variables.demandaId], context.previousDemanda);
      }
    },
  });
};
